import { useState, type ReactNode } from "react"
import { CaretDown, CaretRight } from "@phosphor-icons/react"

import { Badge } from "~/components/design-system/components"
import { insightAnswerLabel } from "~/components/demand-2/shared/insightAnswerLabel"
import { LevelDisclosure } from "~/components/demand-2/shared/LevelDisclosure"
import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { Tooltip } from "~/components/reusable/Tooltip"
import {
  type FramingQuestionId,
  type FramingResolution,
  type FramingSlice,
  type SliceAnswerQuestion,
  sliceKey,
} from "~/utils/demandFraming"
import { partitionSlices, type SliceCoverage, sliceNeeds } from "~/utils/demandInsightSlice"
import { formatNumber } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"

/**
 * One sales slice on one insight question: its answer while closed (violet when it answers on its
 * own), `children` when open. A function so only an open slice mounts its reads.
 */
export const SliceAnswerLine = ({
  label,
  question,
  resolution,
  children,
}: {
  label: string
  question: FramingQuestionId
  resolution: FramingResolution
  children: () => ReactNode
}) => {
  const [open, setOpen] = useState(false)
  const Caret = open ? CaretDown : CaretRight
  const { kind } = useDemand2Scope()
  const chosen = insightAnswerLabel(question, resolution, { kind, scope: "node" })
  const answerLabel = chosen != null && resolution.isDefault ? `${chosen} (default)` : chosen
  return (
    <div className="bg-base">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return
          e.preventDefault()
          setOpen((o) => !o)
        }}
        className="flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left hover:bg-subtle"
      >
        <Caret className="size-3.5 shrink-0 text-fg-subtle" />
        <span className="min-w-0 flex-1 truncate paragraph-sm text-fg-base">{label}</span>
        {answerLabel != null && (
          <Badge
            variant={resolution.inherited ? "gray" : "violet"}
            size="xs"
            className="shrink-0"
            title={
              resolution.inherited
                ? "Follows the wider answer. Open it to answer for it alone."
                : "Answers on its own. Open it to change or clear that."
            }
          >
            {answerLabel}
          </Badge>
        )}
      </div>
      {open && (
        <div className="flex flex-col gap-3 border-t border-base bg-subtle px-3 py-3">
          {children()}
        </div>
      )}
    </div>
  )
}

export const ownSlicesSummary = (own: number, total: number): string =>
  own === 0 ? "All follow the overall answer" : `${own} of ${total} answer on their own`

const listFormat = new Intl.ListFormat("en", { type: "conjunction" })

const LISTED_EMPTY = 3

/**
 * "No pre-orders in retail and ecommerce." Past a few names, the rest behind an "N more" hover, as
 * `ImpactChangeList` does.
 */
const EmptySlicesNote = ({
  question,
  labels,
}: {
  question: SliceAnswerQuestion
  labels: readonly string[]
}) => {
  const what = formatName(sliceNeeds(question) === "preorders" ? "preorder" : "sale", true)
  if (labels.length <= LISTED_EMPTY) return <>{`No ${what} in ${listFormat.format(labels)}.`}</>
  const rest = labels.slice(LISTED_EMPTY)
  return (
    <>
      {`No ${what} in ${labels.slice(0, LISTED_EMPTY).join(", ")} and `}
      <Tooltip
        body={
          <div className="flex flex-col gap-1">
            {rest.map((label) => (
              <span key={label} className="paragraph-sm text-fg-subtle">
                {label}
              </span>
            ))}
          </div>
        }
      >
        <span className="cursor-help underline decoration-dotted decoration-fg-muted underline-offset-4">
          {formatNumber(rest.length)} more
        </span>
      </Tooltip>
      .
    </>
  )
}

type ListedSlice = { slice: FramingSlice; label: string; coverage?: SliceCoverage }

/**
 * A question's sales slices under one disclosure: a row per slice it has something to read on, and
 * one line naming the rest, whose figures would only be the collection's scaled by baseline share.
 */
export const SliceAnswerList = <T extends ListedSlice>({
  title,
  summary,
  question,
  slices,
  resolutionFor,
  children,
}: {
  title: string
  /** From the count of slices answering on their own, and of all slices. */
  summary: (own: number, total: number) => string
  question: SliceAnswerQuestion
  slices: readonly T[]
  resolutionFor: (slice: FramingSlice) => FramingResolution
  /** One row's body, mounted only while that row is open. */
  children: (row: T) => ReactNode
}) => {
  const { rows, emptyLabels, own } = partitionSlices(
    question,
    slices,
    (slice) => !resolutionFor(slice).inherited,
  )
  return (
    // Only the slices with a row: one listed as empty can't be answered, so it never answers alone.
    <LevelDisclosure title={title} summary={summary(own, rows.length)}>
      <div className="divide-y divide-border-base overflow-hidden rounded-md border border-base bg-base">
        {rows.map((row) => (
          <SliceAnswerLine
            key={sliceKey(row.slice)}
            label={row.label}
            question={question}
            resolution={resolutionFor(row.slice)}
          >
            {() => children(row)}
          </SliceAnswerLine>
        ))}
        {emptyLabels.length > 0 && (
          <div className="px-3 py-2 paragraph-sm text-fg-subtle">
            <EmptySlicesNote question={question} labels={emptyLabels} />
          </div>
        )}
      </div>
    </LevelDisclosure>
  )
}
